import { useState, useMemo } from "react";
import {
  TextField, FormControl, InputLabel, Select, MenuItem, Autocomplete,
} from "@mui/material";
import { IJobGrade, JOB_GRADES } from "data/job_grades";
import { flattenStructure, loadStructure, StructureNode } from "./StructureEditor/treeUtils";
import { loadTree, saveTree, updateNode, OrgNode, MemberStatus } from "./OrgChart";
import styles from "./OrgChart/styles.module.scss";

function loadCustomGrades(): IJobGrade[] {
  try { return JSON.parse(localStorage.getItem("job_grades_custom_v1") || "[]"); } catch { return []; }
}

function findStructureNode(nodes: StructureNode[], id: string): StructureNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const f = findStructureNode(n.children, id);
    if (f) return f;
  }
  return null;
}

function collectLeaves(node: StructureNode): Array<{ id: string; name: string }> {
  if (node.children.length === 0) return [{ id: node.id, name: node.name }];
  return node.children.flatMap(collectLeaves);
}

function findOrgUnitByName(nodes: OrgNode[], name: string): string {
  for (const n of nodes) {
    if (n.type === "unit" && n.name.toLowerCase().trim() === name.toLowerCase().trim()) return n.id;
    const found = findOrgUnitByName(n.children, name);
    if (found) return found;
  }
  return "";
}

function findFirstOrgUnit(nodes: OrgNode[]): string {
  for (const n of nodes) {
    if (n.type === "unit") return n.id;
    const found = findFirstOrgUnit(n.children);
    if (found) return found;
  }
  return "";
}

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const AddPersonPanel = ({ onClose, onSuccess }: Props) => {
  const [personFullName, setPersonFullName] = useState("");
  const [email, setEmail] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [unitStructureId, setUnitStructureId] = useState("");
  const [positionId, setPositionId] = useState("");

  const allGrades = useMemo(() => [...JOB_GRADES, ...loadCustomGrades()], []);
  const urzedniczeGrades = useMemo(() => allGrades.filter((g) => g.group === "Urzędnicze"), [allGrades]);
  const stanowiskoGrades = useMemo(
    () => allGrades.filter((g) => g.group === "Kierownicze" || g.group === "Organy JST"),
    [allGrades],
  );
  const orgUnits = useMemo(
    () => flattenStructure(loadStructure()).filter((u) => u.hasChildren),
    [],
  );

  const positionsForUnit = useMemo((): Array<{ id: string; name: string }> => {
    if (!unitStructureId) return [];
    const parent = findStructureNode(loadStructure(), unitStructureId);
    if (!parent) return [];
    return parent.children.flatMap(collectLeaves);
  }, [unitStructureId]);

  const selectedUnitOption = orgUnits.find((u) => u.id === unitStructureId) ?? null;

  const canSave = personFullName.trim().length > 0;

  const handleSave = () => {
    if (!canSave) return;

    const structure = loadStructure();
    const structUnit = findStructureNode(structure, unitStructureId);

    const orgTree = loadTree();
    let targetUnitId = structUnit
      ? findOrgUnitByName(orgTree, structUnit.name)
      : "";
    if (!targetUnitId) targetUnitId = findFirstOrgUnit(orgTree);

    const positionName =
      positionsForUnit.find((p) => p.id === positionId)?.name ||
      stanowiskoGrades.find((g) => g.id === positionId)?.name ||
      personFullName.trim();

    const newNode: OrgNode = {
      id: `p-${Date.now()}`,
      type: "position",
      name: positionName,
      expanded: false,
      children: [],
      personFullName: personFullName.trim(),
      email: email.trim() || undefined,
      gradeId: gradeId || undefined,
      unitStructureId: unitStructureId || undefined,
      positionId: positionId || undefined,
      memberStatus: "wpisany" as MemberStatus,
    };

    if (targetUnitId) {
      const updated = updateNode(orgTree, targetUnitId, (u) => ({
        ...u,
        expanded: true,
        children: [...u.children, newNode],
      }));
      saveTree(updated);
    } else {
      saveTree([...orgTree, newNode]);
    }

    onSuccess();
    onClose();
  };

  return (
    <div className={`${styles.editPanel} ${styles.addPersonPanelWide}`}>
      <div className={styles.editPanelHeader}>
        <span className={styles.editPanelLabel}>Nowa osoba</span>
        <button className={styles.closeBtn} onClick={onClose}>✕</button>
      </div>

      <div className={styles.editPanelBody}>
        <div className={styles.posCard}>
          <TextField
            label="Imię i Nazwisko"
            value={personFullName}
            onChange={(e) => setPersonFullName(e.target.value)}
            variant="standard"
            fullWidth
            autoFocus
          />

          <TextField
            label="E-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            variant="standard"
            fullWidth
            inputProps={{ inputMode: "email" }}
          />

          <FormControl variant="standard" fullWidth>
            <InputLabel>Stopień służbowy</InputLabel>
            <Select
              value={gradeId}
              label="Stopień służbowy"
              onChange={(e) => setGradeId(e.target.value)}
            >
              <MenuItem value=""><em>Brak</em></MenuItem>
              {urzedniczeGrades.map((g) => (
                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            options={orgUnits}
            value={selectedUnitOption}
            onChange={(_, val) => {
              setUnitStructureId(val?.id ?? "");
              setPositionId("");
            }}
            getOptionLabel={(opt) => opt.name}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderOption={(props, opt) => (
              <li {...props} key={opt.id}>
                {opt.depth > 0 && (
                  <span style={{ color: "#bdbdbd", marginRight: 4, flexShrink: 0 }}>
                    {"—".repeat(opt.depth)}{" "}
                  </span>
                )}
                {opt.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Jednostka organizacyjna" variant="standard" />
            )}
            noOptionsText="Brak wyników"
            clearText="Wyczyść"
            openText="Otwórz"
            fullWidth
          />

          {unitStructureId && (
            <FormControl variant="standard" fullWidth>
              <InputLabel>Stanowisko</InputLabel>
              <Select
                value={positionId}
                label="Stanowisko"
                onChange={(e) => setPositionId(e.target.value)}
              >
                <MenuItem value=""><em>Brak</em></MenuItem>
                {positionsForUnit.length > 0 ? (
                  positionsForUnit.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))
                ) : (
                  stanowiskoGrades.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      <span style={{ color: "#9e9e9e", fontSize: 11, marginRight: 4 }}>{g.group} ·</span>
                      {g.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}

          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={!canSave}
          >
            Dodaj osobę
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPersonPanel;
